'use strict';

/**
 * ScoreCard class contains logic required by Score Card feature (ScoreCardHome and ScoreCardTheme etc.)
 *
 * @version 1.0.0
 */
class ScoreCard {

	/**
	 * Constructor method
	 */
	constructor() {
		this.initInfo();
		this.initForm();
		this.initSeriesForm();
		this.initNewSeriesForm();
		this.initInfoForm();
		this.initInputCustom();
		this.initAnswerExtra();
		this.initExitConfirm();
		this.initResetAnswer();
		this.initViewPreviousAnswer();
		this.initUserNotes();
		this.initSortableTables();
		this.initSuggestionToggles();
		this.initScoreCardYearSelect();
	}

	/**
	 * Init score card info
	 */
	initInfo() {

		// find toggle buttons
		const buttons = document.querySelectorAll('.js-sc-info-toggle');
		if (!buttons.length) return;

		// add expand event to buttons
		Array.prototype.forEach.call(buttons, button => {
			let target = document.getElementById(button.getAttribute('aria-controls'));
			if (target) {
				button.onclick = () => {
					let expanded = button.getAttribute('aria-expanded') == 'true' || false;
					button.setAttribute('aria-expanded', !expanded);
					target.hidden = expanded;
				}
			}
		});
	}

	/**
	 * Init score card form
	 */
	initForm() {

		// find form
		const form = document.getElementById('js-sc-form');
		if (!form) return;

		// skip non-answered fields
		form.addEventListener('submit', () => {
			document.querySelectorAll('.js-empty-answer').forEach(input => {
				input.setAttribute('disabled', 'disabled');
			});
		});

		// alt submit method
		const salt = document.getElementById('js-sc-submit-alt');
		if (salt) {
			salt.addEventListener('click', () => {
				document.getElementById('js-sc-after-save').value = 2;
			});
		}
	}

	/**
	 * Init score card series form
	 *
	 * This form makes it possible to switch between existing score card series, or alterntively create a new one.
	 */
	initSeriesForm() {

		// find form
		const seriesForm = document.getElementById('js-sc-series-form');
		if (!seriesForm) return;

		// current value
		const seriesSelect = document.getElementById('js-sc-series-select');
		const currentSeries = seriesSelect.querySelector('option[selected="selected"]').getAttribute('value')

		// confirm selection
		seriesSelect.addEventListener('change', (event) => {
			if (seriesSelect.value == 0) {
				event.preventDefault();
				window._site.confirm(
					seriesSelect.getAttribute('data-confirm-title'),
					seriesSelect.getAttribute('data-confirm-text-new'),
					seriesSelect.getAttribute('data-confirm-button-text'),
					seriesSelect.getAttribute('data-confirm-cancel-button-text'),
					function() {
						seriesForm.submit();
					},
					function() {
						seriesSelect.value = currentSeries;
						return false;
					}
				);
			} else if (seriesSelect.value != currentSeries) {
				event.preventDefault();
				window._site.confirm(
					seriesSelect.getAttribute('data-confirm-title'),
					seriesSelect.getAttribute('data-confirm-text-change'),
					seriesSelect.getAttribute('data-confirm-button-text'),
					seriesSelect.getAttribute('data-confirm-cancel-button-text'),
					function() {
						seriesForm.submit();
					},
					function() {
						seriesSelect.value = currentSeries;
						return false;
					}
				);
			}
		});
	}

	/**
	 * Init score card new series form
	 *
	 * This is a simplified version of series select form, only allowing user to start a new series.
	 */
	initNewSeriesForm() {

		// find form
		const seriesForm = document.getElementById('js-sc-new-series-form');
		if (!seriesForm) return;

		seriesForm.addEventListener('submit', (event) => {
			event.preventDefault();
			window._site.confirm(
				seriesForm.getAttribute('data-confirm-title'),
				seriesForm.getAttribute('data-confirm-text-new'),
				seriesForm.getAttribute('data-confirm-button-text'),
				seriesForm.getAttribute('data-confirm-cancel-button-text'),
				function() {
					seriesForm.submit();
				},
				function() {
					return false;
				}
			);
		});
	}

	/**
	 * Init score card contact information form
	 */
	initInfoForm() {

		// find form
		const form = document.getElementById('js-sc-info-form');
		if (!form) return;

		// find form toggle
		const formToggle = document.getElementById('js-sc-info-form-toggle');
		formToggle.removeAttribute('hidden');
		formToggle.addEventListener('click', () => {
			formToggle.setAttribute('hidden', '');
			form.querySelectorAll('.js-sc-info-form-input').forEach(i => {
				i.removeAttribute('readonly');
				if (i.nodeName === 'SELECT') {
					i.removeAttribute('disabled');
				}
			});
			form.querySelector('input[type="submit"]').removeAttribute('hidden');
			form.querySelector('input:not([type="hidden"])').focus();
		});

		// form submit event
		form.addEventListener('submit', e => {

			e.preventDefault();

			// parse data
			let data = [];
			form.querySelectorAll('.js-sc-info-form-input').forEach(i => {
				i.setAttribute('readonly', '');
				data.push(encodeURIComponent(i.getAttribute('name')) + '=' + encodeURIComponent(i.value));
				if (i.nodeName === 'SELECT') {
					i.setAttribute('disabled', '')
				}
			});
			form.querySelectorAll('input[type="hidden"]').forEach(i => {
				data.push(encodeURIComponent(i.getAttribute('name')) + '=' + encodeURIComponent(i.value));
			});

			// send request
			const xhr = new XMLHttpRequest();
			xhr.addEventListener('load', () => {
				try {
					const response = JSON.parse(xhr.responseText);
					if (response.success) {
						// success
					} else {
						alert(response.message);
					}
				} catch (error) {
					alert(form.getAttribute('data-general-error'));
				}
			});
			xhr.open('POST', form.getAttribute('action'));
			xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
			xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
			xhr.send(data.join('&').replace(/%20/g, '+'));

			// reset UI
			form.querySelector('input[type="submit"]').setAttribute('hidden', '');
			formToggle.removeAttribute('hidden');
		});

		autosize(document.querySelectorAll('.js-autosize'));
	}

	/**
	 * Init score card input customizations
	 */
	initInputCustom() {

		// rating scale questions
		const ratingScaleInputs = document.querySelectorAll('.rating-scale-question input[type=range]');
		if (ratingScaleInputs.length) {
			ratingScaleInputs.forEach(input => {
				input.addEventListener('change', event => {
					if (event.isResetButtonClick) return;
					input.setAttribute('value', input.value);
					input.classList.remove('js-empty-answer');
				});
				input.addEventListener('mousedown', () => {
					input.dispatchEvent(
						new CustomEvent('change', {
							bubbles: true,
							cancelable: true
						})
					);
				});
				input.addEventListener('focus', () => {
					input.dispatchEvent(
						new CustomEvent('change', {
							bubbles: true,
							cancelable: true
						})
					);
				});
			});
		}
	}

	/**
	 * Init score card answer extra
	 */
	initAnswerExtra() {

		// find questions with extra content
		const questions = document.querySelectorAll('.js-sc-question[data-extra]');
		if (!questions.length) return;

		// add event listeners
		questions.forEach(question => {
			question.addEventListener('change', event => {
				if (event.target.nodeName !== 'INPUT') return;
				const info = question.querySelector('.infobox');
				info.setAttribute('hidden', '');
				if (event.isResetButtonClick || event.isViewPreviousButtonClick) return;
				const infoText = info.querySelector('.infobox__text');
				const value = parseInt(event.target.value);
				const extra = JSON.parse(question.getAttribute('data-extra'));
				extra.forEach(extraItem => {
					if (extraItem[0].indexOf(value) >= 0) {
						infoText.innerHTML = extraItem[1];
						info.removeAttribute('hidden');
						return;
					}
				});
			});
			question.querySelectorAll('input[type="range"]:not(.js-empty-answer), input[type="radio"]:checked').forEach(input => {
				input.dispatchEvent(
					new CustomEvent('change', {
						bubbles: true,
						cancelable: true
					})
				);
			});
		});
	}

	/**
	 * Init exit confirmation (in case user has made changes and attempts to exit without saving)
	 */
	initExitConfirm() {

		// find inputs
		const inputs = document.querySelectorAll('.js-sc-input');

		// stash for input values
		const inputValues = {};

		// add input event listeners
		inputs.forEach(input => {
			if (input.type != 'radio' || input.checked) {
				inputValues[input.name] = input.classList.contains('js-empty-answer') ? null : input.value;
			} else if (!Object.prototype.hasOwnProperty.call(inputValues, input.name)) {
				inputValues[input.name] = null;
			}
			input.addEventListener('change', () => {
				input.classList.remove('js-sc-input--changed');
				if (input.type == 'radio') {
					document.querySelectorAll('input[name="' + input.name + '"]').forEach(radioInput => {
						radioInput.classList.remove('js-sc-input--changed');
					})
				}
				if (input.value != inputValues[input.name]) {
					input.classList.add('js-sc-input--changed');
				}
			});
		});

		// add submit button event listeners
		document.querySelectorAll('.js-sc-submit').forEach(submit => {
			submit.addEventListener('click', () => {
				document.querySelectorAll('.js-sc-input--changed').forEach(input => {
					input.classList.remove('js-sc-input--changed');
				});
			})
		});

		// add document event listener (exit confirmation)
		window.onbeforeunload = event => {
			if (document.querySelector('.js-sc-input--changed')) {
				// cancel the event - required in all browsers, triggers confirm prompt in Firefox
				event.preventDefault();
				// set return value - required in Chrome to show confirm prompt, for more details see
				// https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onbeforeunload
				event.returnValue = '';
			}
		};
	}

	/**
	 * Display user notes
	 */
	initUserNotes() {
		const userNotesButton = document.getElementById('user-notes');
		const userNotesModal = document.getElementById('user-notes-modal');
		if (!userNotesButton || !userNotesModal) return;

		userNotesButton.addEventListener('click', event => {
			event.preventDefault();
			userNotesModal.classList.add("flex");
			userNotesModal.classList.remove("hidden");
		});

		const userNotesClose = document.getElementById('user-notes--close');
		if (!userNotesClose) return;

		userNotesClose.addEventListener('click', event => {
			event.preventDefault();
			userNotesModal.classList.remove("flex");
			userNotesModal.classList.add("hidden");
		});
	}

	/**
	 * Reset score card answer
	 */
	initResetAnswer() {

		const resetButtons = document.querySelectorAll('.js-reset-answer');
		if(!resetButtons.length) return;

		resetButtons.forEach(button => {
			button.addEventListener('click', event => {
				event.preventDefault();
				const inputs = button.closest('.js-sc-question').querySelectorAll('.js-sc-input');
				inputs.forEach(input => {
					if (input.closest('.js-previous-answer')) return;
					if (input.type == 'radio' || input.type == 'checkbox') {
						input.checked = false;
					} else {
						input.value = null;
					}
					if (input.type == 'range') {
						input.classList.add('js-empty-answer');
						input.removeAttribute('value');
					}
					const changeEvent = new CustomEvent('change', {
						'bubbles': true
					});
					changeEvent.isResetButtonClick = true;
					input.dispatchEvent(changeEvent);
				});
			});
		});
	}

	/**
	 * Initialize show previous answer
	 */
	initViewPreviousAnswer() {
		const prevButtons = document.querySelectorAll('.js-view-previous-answer');
		if(!prevButtons.length) return;

		prevButtons.forEach(button => {
			button.addEventListener('click', event => {

				button.setAttribute('data-view-previous', button.getAttribute('data-view-previous') == 0 ? 1 : 0);
				if (button.getAttribute('data-view-previous') == 1) {
					button.querySelector('.js-view-previous-answer__label--hide').classList.remove('hidden');
					button.querySelector('.js-view-previous-answer__label--show').classList.add('hidden');
				} else {
					button.querySelector('.js-view-previous-answer__label--hide').classList.add('hidden');
					button.querySelector('.js-view-previous-answer__label--show').classList.remove('hidden');
				}

				event.preventDefault();

				if (button.getAttribute('data-view-previous') == 0) {
					button.closest('.question').querySelector('.js-previous-answer').remove();
					return;
				}

				const prevAnswer = button.closest('.question').getAttribute('data-previous-answer');

				const prevAnswerContainer = document.createElement('div');
				prevAnswerContainer.setAttribute('tabindex', -1);
				prevAnswerContainer.classList.add('js-previous-answer');
				prevAnswerContainer.classList.add('grayscale');
				prevAnswerContainer.classList.add('opacity-75');
				prevAnswerContainer.classList.add('border-t');
				prevAnswerContainer.classList.add('border-black');
				prevAnswerContainer.classList.add('border-opacity-25');
				prevAnswerContainer.classList.add('mt-double');
				button.closest('.question__buttons').insertAdjacentElement('afterend', prevAnswerContainer);

				const inputs = button.closest('.question__buttons').previousElementSibling;
				const clonedInputs = inputs.cloneNode(true);

				clonedInputs.querySelectorAll('label').forEach(label => {
					label.removeAttribute('for');
				});

				clonedInputs.querySelectorAll('.js-sc-input').forEach(input => {
					if (input.hasAttribute('name')) {
						input.setAttribute('name', input.getAttribute('name') + '__clone');
					}
					if (input.hasAttribute('id')) {
						input.setAttribute('id', input.getAttribute('id') + '__clone');
					}
					input.disabled = true;
					input.readonly = true;
					if (input.type == 'radio' || input.type == 'checkbox') {
						input.checked = input.value == prevAnswer;
					} else {
						input.value = prevAnswer;
					}
					if (input.type == 'range') {
						input.setAttribute('value', prevAnswer);
					}
					const changeEvent = new CustomEvent('change', {
						'bubbles': true
					});
					changeEvent.isViewPreviousButtonClick = true;
					input.dispatchEvent(changeEvent);
				});

				prevAnswerContainer.appendChild(clonedInputs);
				prevAnswerContainer.focus();
			});
		});
	}

	/**
	 * Initialize sortable tables
	 *
	 * https://stackoverflow.com/questions/14267781/sorting-html-table-with-javascript
	 *
	 * @param {Object} context
	 */
	initSortableTables(context = document) {
		const getCellValue = (tr, idx) => tr.children[idx].textContent;
		const comparer = (idx, asc) => (a, b) => ((v1, v2) =>
			v1 !== '' && v2 !== '' && !isNaN(v1) && !isNaN(v2) ? v1 - v2 : v1.toString().localeCompare(v2)
		)(getCellValue(asc ? a : b, idx), getCellValue(asc ? b : a, idx));
		context.querySelectorAll('.js-sortable-table').forEach(table => {
			table.querySelectorAll('th').forEach(th => th.addEventListener('click', (() => {
				const table = th.closest('table');
				table.querySelectorAll('.js-sortable-table__sort').forEach(th => {
					th.classList.remove('js-sortable-table__sort')
					th.classList.remove('js-sortable-table__sort--asc')
					th.classList.remove('js-sortable-table__sort--desc')
				})
				if (th.hasAttribute('data-sort-direction')) {
					this.asc = th.getAttribute('data-sort-direction') == 'asc';
					th.removeAttribute('data-sort-direction');
				}
				th.classList.add('js-sortable-table__sort')
				th.classList.add('js-sortable-table__sort--' + (this.asc ? 'asc' : 'desc'))
				const tbody = table.querySelector('tbody')
				Array.from((tbody || table).querySelectorAll('tr'))
					.sort(comparer(Array.from(th.parentNode.children).indexOf(th), this.asc = !this.asc))
					.forEach(tr => (tbody || table).appendChild(tr))
				const download = table.nextElementSibling
				if (download && download.hasAttribute('download') && download.getAttribute('href').match('&sort=')) {
					let sortKey = th.getAttribute('data-column-key')
					let sortModifier = this.asc ? '' : '-';
					if (th.hasAttribute('data-column-invert-sort')) {
						sortModifier = sortModifier == '' ? '-' : '';
					}
					download.setAttribute('href', download.getAttribute('href').replace(
						new RegExp('&sort=.*'),
						'&sort=' + sortModifier + sortKey
					))
				}
			})))
			if (table.hasAttribute('data-sort')) {
				const sortColumnNum = parseInt(table.getAttribute('data-sort'));
				if (sortColumnNum >= 0) {
					const sortColumn = table.querySelector('thead th:nth-of-type(' + sortColumnNum + ')');
					if (sortColumn) {
						sortColumn.setAttribute('data-sort-direction', sortColumn.classList.contains('js-sortable-table__sort--asc') ? 'desc' : 'asc');
						sortColumn.dispatchEvent(new Event('click'));
					}
				}
			}
		})
	}

	/**
	 * Initialize suggestion toggles
	 */
	initSuggestionToggles() {
		document.addEventListener('click', event => {
			const toggle = event.target.closest('th');
			if (!toggle || !toggle.classList.contains('js-show-suggestions')) return;
			document.getElementById('modal-for-suggestions-title').innerText = toggle.querySelector('.js-suggestions-title').innerText.trim();
			document.getElementById('modal-for-suggestions-content').innerHTML = toggle.querySelector('.js-suggestions-content').innerHTML;
			MicroModal.show('modal-for-suggestions');
		});
	}

	/**
	 * Initialize score card year select
	 */
	initScoreCardYearSelect() {

		const yearSelect = document.getElementById('js-score-card-year-select');
		if (!yearSelect) return;

		const form = yearSelect.closest('form');
		const table = form.nextElementSibling.querySelector('table');

		yearSelect.addEventListener('change', event => {
			const year = event.target.value;
			table.querySelectorAll('tbody tr:not([data-year="' + year + '"]').forEach(row => {
				row.setAttribute('hidden', '');
			});
			table.querySelectorAll('tbody tr[data-year="' + year + '"]').forEach(row => {
				row.removeAttribute('hidden');
			});
			if (table.classList.contains('js-sortable-table')) {
				let sortColumn = table.querySelector('th.js-sortable-table__sort');
				if (sortColumn) {
					sortColumn.setAttribute('data-sort-direction', sortColumn.classList.contains('js-sortable-table__sort--asc') ? 'asc' : 'desc');
					sortColumn.dispatchEvent(new Event('click'));
				} else if (table.getAttribute('data-sort')) {
					const sortColumnNum = parseInt(table.getAttribute('data-sort'));
					if (sortColumnNum >= 0) {
						sortColumn = table.querySelector('thead th:nth-of-type(' + sortColumnNum + ')');
						if (sortColumn) {
							sortColumn.setAttribute('data-sort-direction', sortColumn.classList.contains('js-sortable-table__sort--asc') ? 'desc' : 'asc');
							sortColumn.dispatchEvent(new Event('click'));
						}
					}
				}
			}
			form.scrollIntoView({
				behavior: 'smooth'
			});
			table.focus({
				preventScroll: true
			});
		});

		yearSelect.dispatchEvent(new Event('change'));
	}
}

window._scoreCard = new ScoreCard();
